import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, MapPin, Mail, Phone, Clock } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useSiteContent, pickLang } from "../lib/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLang();
  const { content } = useSiteContent();
  const contact = content?.contact || {};
  const address = pickLang(contact.address, lang, "Bağdat Cad. No: 142, Kadıköy\nİstanbul, Türkiye");
  const phone = contact.phone || "+90 216 123 45 67";
  const email = contact.email || "hello@intouch.consulting";
  const hours = pickLang(contact.workingHours, lang, t.contact.info.hoursBody);
  const [form, setForm] = useState({ name: "", email: "", organization: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  // Honeypot: bot'lar bu gizli alanı doldurur, insanlar doldurmaz
  const [honeypot, setHoneypot] = useState("");

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    // Honeypot dolu ise bot — sessizce başarılı gibi davran
    if (honeypot) {
      setStatus("success");
      setForm({ name: "", email: "", organization: "", subject: "", message: "" });
      return;
    }
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", organization: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      setStatus("error");
      toast.error(t.contact.form.error);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="container-x pt-12 pb-12 md:pt-20 md:pb-12">
        <span className="label-eyebrow">{t.contact.eyebrow}</span>
        <h1 className="mt-5 font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight text-ink max-w-3xl">
          {t.contact.title}
        </h1>
        <p className="mt-6 text-lg md:text-xl text-ink/70 font-light leading-relaxed max-w-2xl">
          {t.contact.lead}
        </p>
      </section>

      <section className="container-x pb-24 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Info */}
          <aside className="lg:col-span-5 flex flex-col gap-6">
            <InfoCard icon={MapPin} title={t.contact.info.addressTitle}>
              <span className="whitespace-pre-line">{address}</span>
            </InfoCard>
            <InfoCard icon={Phone} title={t.contact.info.phoneTitle}>
              <a href={`tel:${(phone || "").replace(/\s+/g, "")}`} className="hover:text-terracotta-400 transition-colors">{phone}</a>
            </InfoCard>
            <InfoCard icon={Mail} title={t.contact.info.emailTitle}>
              <a href={`mailto:${email}`} className="hover:text-terracotta-400 transition-colors">{email}</a>
            </InfoCard>
            <InfoCard icon={Clock} title={t.contact.info.hoursTitle}>
              {hours}
            </InfoCard>
          </aside>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="surface-card p-7 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              <Field label={t.contact.form.name} required>
                <input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={update("name")}
                  required
                  type="text"
                  className="form-input"
                />
              </Field>
              <Field label={t.contact.form.email} required>
                <input
                  data-testid="contact-email"
                  value={form.email}
                  onChange={update("email")}
                  required
                  type="email"
                  className="form-input"
                />
              </Field>
              <Field label={t.contact.form.organization}>
                <input
                  data-testid="contact-org"
                  value={form.organization}
                  onChange={update("organization")}
                  type="text"
                  className="form-input"
                />
              </Field>
              <Field label={t.contact.form.subject}>
                <input
                  data-testid="contact-subject"
                  value={form.subject}
                  onChange={update("subject")}
                  type="text"
                  className="form-input"
                />
              </Field>
              <div className="md:col-span-2">
                <Field label={t.contact.form.message} required>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={update("message")}
                    required
                    rows={6}
                    className="form-input resize-none"
                  />
                </Field>
              </div>

              {/* Honeypot — botlar için gizli alan, insanlar görmez */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
                <label>
                  Website
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </label>
              </div>

              <div className="md:col-span-2 flex items-center justify-between gap-4 flex-wrap pt-2">
                <p className="text-xs text-ink/55 font-light max-w-md">
                  {status === "success" ? (
                    <span data-testid="contact-success" className="text-sage-700">{t.contact.form.success}</span>
                  ) : status === "error" ? (
                    <span data-testid="contact-error" className="text-terracotta-500">{t.contact.form.error}</span>
                  ) : null}
                </p>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={status === "sending"}
                  className="btn-accent disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "sending" ? t.contact.form.sending : t.contact.form.submit}
                  <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <style>{`
        .form-input {
          width: 100%;
          background: #FBFAF6;
          border: 1px solid #E5E0D8;
          border-radius: 0.85rem;
          padding: 0.85rem 1rem;
          font-family: 'Outfit', sans-serif;
          font-size: 0.95rem;
          color: #2C3531;
          font-weight: 300;
          transition: all 0.2s ease;
        }
        .form-input:focus {
          outline: none;
          border-color: #839788;
          background: #FFFFFF;
          box-shadow: 0 0 0 4px rgba(131, 151, 136, 0.12);
        }
      `}</style>
    </div>
  );
}

function InfoCard({ icon: Icon, title, children }) {
  return (
    <div className="flex items-start gap-4 p-5 rounded-2xl bg-cream-200/40 border border-cream-300/60">
      <span className="w-11 h-11 shrink-0 rounded-xl bg-white flex items-center justify-center text-terracotta-400 border border-cream-300/60">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
      </span>
      <div>
        <span className="block text-xs uppercase tracking-[0.22em] text-ink/55 font-medium">{title}</span>
        <div className="mt-1.5 text-base text-ink font-light leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.22em] text-ink/55 font-medium mb-2">
        {label}{required && <span className="text-terracotta-400">*</span>}
      </span>
      {children}
    </label>
  );
}
